import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Box } from '@theme-ui/components';
import { useQuery } from '@apollo/client';

import HookFormErrors from '@app/components/HookFormErrors';
import useValidationMessage from '@app/hooks/useValidationMessage';

import { AddressFormFields } from '../Address.constants';
import messages from '../Address.messages';
import { FormVisibilityState } from '../Address.types';
import type { CustomerAndCartAddressesQuery } from './__generated__/CustomerAndCartAddressesQuery';
import AddAddressButton from './AddAddressButton';
import AddressCard from './AddressCard';
import { CUSTOMER_AND_CART_ADDRESSES } from './CustomerAddresses.queries';
import {
  getCartAddress,
  getCustomerAddresses,
  getRelevantAddresses,
} from './helpers';

const CustomerAddresses = () => {
  const { data, loading } = useQuery<CustomerAndCartAddressesQuery>(
    CUSTOMER_AND_CART_ADDRESSES,
    {
      fetchPolicy: 'network-only',
      ssr: false,
    },
  );
  const cartAddress = useMemo(() => getCartAddress(data), [data]);
  const customerAddresses = useMemo(() => getCustomerAddresses(data), [data]);
  const addresses = useMemo(
    () => getRelevantAddresses(customerAddresses, cartAddress),
    [cartAddress, customerAddresses],
  );

  const {
    setValue,
    formState: { errors },
    control,
    clearErrors,
  } = useFormContext();

  const { msg } = useValidationMessage(messages);

  const formVisibility = useWatch({
    control,
    name: AddressFormFields.FormVisibility,
  });

  useEffect(() => {
    if (addresses?.length) {
      setValue(AddressFormFields.FormVisibility, FormVisibilityState.HIDDEN);

      if (cartAddress) {
        setValue(AddressFormFields.Selected, cartAddress);
      }
    } else {
      setValue(AddressFormFields.FormVisibility, FormVisibilityState.SHOWN);
    }
  }, [addresses, cartAddress, setValue, loading]);

  if (loading || !addresses) {
    return null;
  }

  return (
    <>
      <HookFormErrors fields={[AddressFormFields.Selected]} />

      <Controller
        name={AddressFormFields.Selected}
        defaultValue=""
        rules={{
          required: {
            value: formVisibility === FormVisibilityState.HIDDEN,
            message: msg({
              type: 'required',
              key: 'selected',
            }),
          },
        }}
        render={({ field: { onChange, value } }) => (
          <>
            {addresses.map((address, idx) => (
              <AddressCard
                idx={idx}
                key={address?.id || 'cart-address'}
                hasError={Boolean(errors[AddressFormFields.Selected])}
                address={address}
                selected={address?.id === value?.id}
                onSelect={() => {
                  clearErrors();
                  onChange(address);
                  setValue(
                    AddressFormFields.FormVisibility,
                    FormVisibilityState.HIDDEN,
                  );
                }}
              />
            ))}
          </>
        )}
      />

      <Box sx={{ width: ['100%', '50%'] }} pr={[0, 10]} mb={20}>
        <AddAddressButton />
      </Box>
    </>
  );
};

export default CustomerAddresses;
